// Client-side facet plumbing for `FilterBar` (UI-LAW §4b.4): harvest a facet's
// options from the rows a screen has loaded, apply the selected values, and
// convert between the applied `FilterValue` and its URL search params.
//
// `facetOptions` is lifted verbatim in behaviour from `DataTable`'s
// `getFacetedUniqueValues()` toolbar (the pre-`FilterBar` path): the options
// come from the LOADED rows of a keyset-paginated list, which is why every
// screen using it renders `FilterBar`'s "Loaded rows only" note (§8 — a
// partial narrowing is never presented as a whole-list one). Server-side
// facet params are a separate epic; nothing here implies one.

import { isDateRangeValue, type FilterOption, type FilterValue } from "../ui";

/** Reads the facet value of one row as a display string. */
export type FacetAccessor<Item> = (item: Item) => string;

/** The accessors for a screen's facets, keyed by facet id. */
export type FacetAccessors<Item> = Readonly<
  Record<string, FacetAccessor<Item>>
>;

/**
 * Distinct values of one facet across the loaded rows, with occurrence counts,
 * sorted by label. Empty values are dropped — "no value" is not a facet.
 */
export function facetOptions<Item>(
  items: readonly Item[],
  accessor: FacetAccessor<Item>,
): FilterOption[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const value = accessor(item);
    if (value.length === 0) {
      continue;
    }
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([value, count]) => ({ count, label: value, value }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** True when the item matches every applied facet ("is any of" per facet). */
export function matchesFacets<Item>(
  item: Item,
  accessors: FacetAccessors<Item>,
  value: FilterValue,
): boolean {
  for (const [id, accessor] of Object.entries(accessors)) {
    const selected = value[id];
    if (!Array.isArray(selected) || selected.length === 0) {
      continue;
    }
    if (!selected.includes(accessor(item))) {
      return false;
    }
  }
  return true;
}

/** The loaded rows narrowed by the applied facets. */
export function applyFacets<Item>(
  items: readonly Item[],
  accessors: FacetAccessors<Item>,
  value: FilterValue,
): Item[] {
  return items.filter((item) => matchesFacets(item, accessors, value));
}

/** True when at least one facet is applied. */
export function hasAppliedFacets(value: FilterValue): boolean {
  return Object.values(value).some((entry) =>
    isDateRangeValue(entry)
      ? entry.start !== null || entry.end !== null
      : Array.isArray(entry) && entry.length > 0,
  );
}

/**
 * A `dateRange` facet rides TWO params, `<id>_after` and `<id>_before`, holding
 * ISO timestamps — the sealed convention the list endpoints already speak
 * (`occurred_after` / `created_before`). It deliberately does not ride one
 * comma-joined param like a selection facet: the halves are independently
 * settable, and the API takes them apart anyway.
 */
export type RangeParamId<TFacetId extends string> =
  | `${TFacetId}_after`
  | `${TFacetId}_before`;

function parseBound(raw: string | undefined): Date | null {
  if (raw === undefined || raw.length === 0) {
    return null;
  }
  const parsed = new Date(raw);
  // An unparseable bound is dropped rather than propagated as Invalid Date,
  // which would serialize back out as "Invalid Date" and poison the URL.
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * URL search params -> applied `FilterValue`. Each facet rides one param named
 * after the facet id, multi-value comma-joined (`?voice=elevenlabs,cartesia`).
 */
export function facetSearchToValue<
  TFacetId extends string,
  TRangeFacetId extends string = never,
>(
  search: {
    readonly [Key in TFacetId | RangeParamId<TRangeFacetId>]?: string;
  },
  facetIds: readonly TFacetId[],
  rangeFacetIds: readonly TRangeFacetId[] = [],
): FilterValue {
  const value: FilterValue = {};
  for (const id of facetIds) {
    const raw = search[id];
    if (raw === undefined || raw.length === 0) {
      continue;
    }
    const tokens = raw.split(",").filter((token) => token.length > 0);
    if (tokens.length > 0) {
      value[id] = tokens;
    }
  }
  for (const id of rangeFacetIds) {
    const start = parseBound(search[`${id}_after` as const]);
    const end = parseBound(search[`${id}_before` as const]);
    if (start !== null || end !== null) {
      value[id] = { end, start };
    }
  }
  return value;
}

/**
 * Applied `FilterValue` -> a search patch. Every facet id is present in the
 * patch (cleared facets as `undefined`) so a functional `mergeSearch` drops
 * the params it no longer needs while root-owned scope params survive.
 */
export function facetValueToSearch<
  TFacetId extends string,
  TRangeFacetId extends string = never,
>(
  value: FilterValue,
  facetIds: readonly TFacetId[],
  rangeFacetIds: readonly TRangeFacetId[] = [],
): { [Key in TFacetId | RangeParamId<TRangeFacetId>]?: string } {
  const patch: {
    [Key in TFacetId | RangeParamId<TRangeFacetId>]?: string;
  } = {};
  for (const id of facetIds) {
    const entry = value[id];
    patch[id] =
      Array.isArray(entry) && entry.length > 0 ? entry.join(",") : undefined;
  }
  for (const id of rangeFacetIds) {
    const entry = value[id];
    const range = isDateRangeValue(entry) ? entry : undefined;
    patch[`${id}_after` as const] = range?.start?.toISOString();
    patch[`${id}_before` as const] = range?.end?.toISOString();
  }
  return patch;
}
